import { createContext, useContext, useEffect, useState } from 'react';
import { fetchUserAttributes, getCurrentUser, signOut } from 'aws-amplify/auth';
import { backendConfig } from '../aws-config';

type AuthUser = { username: string; userId: string; email?: string; givenName?: string; familyName?: string };
type AuthContextValue = { user: AuthUser | null; loading: boolean; configured: boolean; refreshUser: () => Promise<void>; logOut: () => Promise<void> };
const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const configured = Boolean(backendConfig.userPoolId && backendConfig.userPoolClientId);

  const refreshUser = async () => {
    if (!configured) { setLoading(false); return; }
    try {
      const [current, attributes] = await Promise.all([getCurrentUser(), fetchUserAttributes()]);
      setUser({ ...current, email: attributes.email, givenName: attributes.given_name, familyName: attributes.family_name });
    } catch { setUser(null); } finally { setLoading(false); }
  };
  useEffect(() => { refreshUser(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logOut = async () => { try { await signOut(); } finally { setUser(null); } };
  return <AuthContext.Provider value={{ user, loading, configured, refreshUser, logOut }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
};
